import { useState } from 'react';
import api from '../lib/api.js';

/**
 * Mr Pilot inline chat — additive component for Job Detail pages.
 * Reads the job's agent_lineage and answers operator questions about
 * the run. Two modes (gated server-side):
 *   - template: rule-based intent matching (always available)
 *   - llm: Claude / OpenAI when enabled on cloud editions
 */
export default function PilotPanel({ jobId }) {
  const [messages, setMessages] = useState([
    {
      role: 'pilot',
      text: 'Hi. I am Mr Pilot. Ask me anything about this run.\n\nTry: "Why did the geocoder flag some rows?" or "What does S-013 mean?"',
      mode: 'template',
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setMessages(m => [...m, { role: 'user', text: q }]);
    setInput('');
    setBusy(true);
    try {
      const res = await api.askPilot(jobId, q);
      setMessages(m => [...m, {
        role: 'pilot',
        text: res.answer,
        mode: res.mode_used,
        citations: res.citations || [],
        followups: res.suggested_followups || [],
      }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'pilot', text: `Could not reach the Pilot agent: ${e.message}`, mode: 'error' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card-pad" style={{ borderColor: '#DBC078', borderWidth: 2, borderStyle: 'solid' }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold" style={{ color: '#DBC078' }}>
            Mr Pilot
          </div>
          <div className="font-semibold text-navy text-sm">Ask me about this run</div>
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
          style={{ backgroundColor: '#032953', color: '#DBC078' }}
        >
          6th agent
        </span>
      </div>

      <div className="space-y-2 mb-3 max-h-80 overflow-y-auto" style={{ background: '#F8F9FB', padding: 12, borderRadius: 8 }}>
        {messages.map((m, i) => (
          <div key={i} className={`text-sm ${m.role === 'user' ? 'text-right' : ''}`}>
            <div
              className="inline-block px-3 py-2 rounded-lg max-w-[88%] whitespace-pre-wrap"
              style={{
                background: m.role === 'user' ? '#032953' : 'white',
                color: m.role === 'user' ? 'white' : '#1F2937',
                border: m.role === 'pilot' ? '1px solid #E5E7EB' : 'none',
                textAlign: 'left',
              }}
            >
              {m.text}
              {m.mode && m.role === 'pilot' && (
                <div className="text-[10px] mt-1 italic" style={{ color: '#6B7280' }}>
                  mode: {m.mode}
                </div>
              )}
            </div>
            {m.role === 'pilot' && m.followups?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {m.followups.map((f, fi) => (
                  <button
                    key={fi}
                    onClick={() => send(f)}
                    className="text-[11px] px-2 py-1 rounded-full"
                    style={{ background: '#DBC078', color: '#032953', fontWeight: 600 }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {busy && <div className="text-xs italic" style={{ color: '#6B7280' }}>Mr Pilot is thinking...</div>}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          className="input flex-1"
          placeholder="Ask Mr Pilot..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          disabled={busy}
        />
        <button
          className="btn btn-primary"
          onClick={() => send()}
          disabled={busy || !input.trim()}
        >
          Ask
        </button>
      </div>
    </div>
  );
}
